import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import FloorPlanBackgroundImageControls from '../../components/FloorPlanBackgroundImageControls.jsx';
import FloorPlanDesignerCanvas from '../../components/FloorPlanDesignerCanvas.jsx';
import FloorPlanDesignerSidebar from '../../components/FloorPlanDesignerSidebar';
import SelectedAreaThreePreview from '../../components/SelectedAreaThreePreview';
import {
  createEmptyDesignerState,
  normalizeDesignerOpenings,
  setWallSettings,
} from '../../lib/floorPlanDesignerState';
import { getFloorPlan2DBounds } from '../../lib/floorPlanContentBounds.js';
import {
  buildDesignerPreviewJson,
  validateDesignerState,
} from '../../lib/floorPlanDesignerToAnalysisJson';
import { WALL_HEIGHT } from '../../lib/selectedAreaThreeModel';
import { computeMaxVisibleHeightM } from '../../lib/floorPlanThreeClipping.js';
import { FIT_PADDING_PX } from '../../lib/floorPlanDesignerConfig.js';
import { floorPlanDesignerConfig } from './configs/floorPlanDesignerConfig';
import FloorPlanWorkspace from './FloorPlanWorkspace/FloorPlanWorkspace.jsx';
import useFloorPlanHistory from './FloorPlanWorkspace/useFloorPlanHistory.js';

function downloadJson(filename, payload) {
  const jsonText = JSON.stringify(payload, null, 2);
  const blob = new Blob([jsonText], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function mergeConfig(config) {
  return {
    ...floorPlanDesignerConfig,
    ...(config || {}),
  };
}

export function getDesignerDrawingBounds(state) {
  return getFloorPlan2DBounds({ state, includeBackground: true });
}

function FloorPlanEditor({
  value,
  onChange,
  config,
  initialState,
  ariaLabel = 'Floor plan workspace',
  exportFilename = 'floor-plan-analysis.json',
  externalError = '',
  externalMessage = '',
  debugJson,
  onClear,
  onExport,
  onCopyJson,
  onViewportActionsChange,
  onRegisterEditorActions,
  sidebarMode = 'design',
  sidebarTopContent = null,
  renderSidebarContent = null,
  activeTool: controlledActiveTool,
  onActiveToolChange,
  onRoomSelectionChange,
  onRenderRoomRequest,
}) {
  const resolvedConfig = useMemo(() => mergeConfig(config), [config]);
  const editorState = useMemo(
    () => normalizeDesignerOpenings(value || initialState || createEmptyDesignerState()),
    [value, initialState],
  );
  const isControlledTool = controlledActiveTool !== undefined;
  const initialTool = resolvedConfig.initialActiveTool || 'wall';
  const [uncontrolledActiveTool, setUncontrolledActiveTool] = useState(initialTool);
  const [activeWorkspaceCategory, setActiveWorkspaceCategory] = useState('build');
  const [activeBuildTool, setActiveBuildTool] = useState(() =>
    initialTool === 'wall' || initialTool === 'room' ? initialTool : null,
  );
  const [libraryMode, setLibraryMode] = useState(
    /** @type {'doors' | 'windows' | 'structures' | null} */ (null),
  );
  const [selectedOpeningPreset, setSelectedOpeningPreset] = useState(
    /** @type {import('../../lib/floorPlanOpeningCatalog.js').FloorPlanOpeningCatalogItem | null} */ (null),
  );
  const libraryRestoreRef = useRef({ category: 'build', buildTool: null });
  const [viewMode, setViewMode] = useState('2d');
  const [isSubcategoryPanelOpen, setIsSubcategoryPanelOpen] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [snapToPoints, setSnapToPoints] = useState(true);
  const [snapGridMeters, setSnapGridMeters] = useState(0.1);
  const [snapTolerancePx, setSnapTolerancePx] = useState(12);
  const [previewJson, setPreviewJson] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [internalMessage, setInternalMessage] = useState('');
  const viewportActionsRef = useRef({});

  const [backgroundEditMode, setBackgroundEditMode] = useState(false);
  const [maxWallHeightM, setMaxWallHeightM] = useState(WALL_HEIGHT);
  const maxVisibleHeightM = useMemo(
    () => computeMaxVisibleHeightM(maxWallHeightM),
    [maxWallHeightM],
  );
  const [visibleHeightM, setVisibleHeightM] = useState(() => computeMaxVisibleHeightM(WALL_HEIGHT));
  const fullVisibleHeightRef = useRef(computeMaxVisibleHeightM(WALL_HEIGHT));

  const handlePresentChange = useCallback(
    (next) => {
      onChange?.(next);
    },
    [onChange],
  );

  const history = useFloorPlanHistory({
    present: editorState,
    onPresentChange: handlePresentChange,
    maxHistory: 10,
  });

  const handleClippingStateReady = useCallback(({ maxWallHeight, maxVisibleHeight }) => {
    if (Number.isFinite(maxWallHeight) && maxWallHeight > 0) {
      setMaxWallHeightM(maxWallHeight);
    }
    if (!Number.isFinite(maxVisibleHeight) || maxVisibleHeight <= 0) return;

    const previousFull = fullVisibleHeightRef.current;
    fullVisibleHeightRef.current = maxVisibleHeight;
    setVisibleHeightM((current) => {
      if (Math.abs(current - previousFull) < 0.001) {
        return maxVisibleHeight;
      }
      return Math.min(Math.max(current, 0), maxVisibleHeight);
    });
  }, []);
  const pendingFitOnModeSwitchRef = useRef(null);
  const threePreviewFitRef = useRef(/** @type {(() => void) | null} */ (null));
  const threePreviewActionsRef = useRef(
    /** @type {{ fit3DToScreen?: () => void; zoom3DIn?: () => void; zoom3DOut?: () => void; resize?: () => void } | null} */ (
      null
    ),
  );
  const canUse3D = resolvedConfig.allow3DPreview !== false;
  const enableBackgroundReferenceImage =
    resolvedConfig.enableBackgroundReferenceImage === true;
  const activeTool = controlledActiveTool ?? uncontrolledActiveTool;
  const runValidation = useCallback(() => validateDesignerState(editorState), [editorState]);
  const runPreviewBuild = useCallback(() => buildDesignerPreviewJson(editorState), [editorState]);

  const handleCategoryChange = useCallback((category) => {
    setActiveWorkspaceCategory(category);
    setIsSubcategoryPanelOpen(true);
  }, []);

  const handleCollapseSubcategoryPanel = useCallback(() => {
    setIsSubcategoryPanelOpen(false);
  }, []);

  useEffect(() => {
    if (viewMode === '3d') {
      requestAnimationFrame(() => {
        threePreviewActionsRef.current?.resize?.();
      });
    }
  }, [isSubcategoryPanelOpen, viewMode]);

  useEffect(() => {
    if (activeTool === 'wall' || activeTool === 'room') {
      setActiveBuildTool(activeTool);
    } else if (activeTool === 'select' || activeTool === 'door' || activeTool === 'window') {
      setActiveBuildTool(null);
    }
  }, [activeTool]);

  const handleActiveToolChange = useCallback(
    (tool) => {
      if (!isControlledTool) {
        setUncontrolledActiveTool(tool);
      }
      onActiveToolChange?.(tool);
    },
    [isControlledTool, onActiveToolChange],
  );

  const handleBuildToolChange = useCallback(
    (tool) => {
      setActiveBuildTool(tool);
      if (tool === 'wall' || tool === 'room') {
        handleActiveToolChange(tool);
        return;
      }
      if (!tool) {
        handleActiveToolChange('select');
      }
    },
    [handleActiveToolChange],
  );

  const handleOpenLibrary = useCallback(
    (mode) => {
      libraryRestoreRef.current = {
        category: activeWorkspaceCategory,
        buildTool: activeBuildTool,
      };
      setLibraryMode(mode);
    },
    [activeBuildTool, activeWorkspaceCategory],
  );

  const handleLibraryBack = useCallback(() => {
    setLibraryMode(null);
    setActiveWorkspaceCategory(libraryRestoreRef.current.category || 'build');
    setActiveBuildTool(libraryRestoreRef.current.buildTool);
  }, []);

  const handleLibrarySelectItem = useCallback(
    (item) => {
      setSelectedOpeningPreset(item);
      setLibraryMode(null);
      setActiveWorkspaceCategory('build');
      setActiveBuildTool(null);
      handleActiveToolChange(item.type);
    },
    [handleActiveToolChange],
  );

  const handleRegisterViewportActions = useCallback(
    (actions) => {
      viewportActionsRef.current = actions;
      onViewportActionsChange?.(actions);
    },
    [onViewportActionsChange],
  );

  const handleViewModeChange = useCallback(
    (mode) => {
      if (mode === '3d') {
        if (!canUse3D) return;
        const preview = runPreviewBuild();
        if (preview.analysisJson) {
          setPreviewJson(preview.analysisJson);
        }
        setPreviewError('');
        pendingFitOnModeSwitchRef.current = '3d';
        setViewMode('3d');
        return;
      }

      pendingFitOnModeSwitchRef.current = '2d';
      setViewMode('2d');
    },
    [canUse3D, runPreviewBuild],
  );

  useEffect(() => {
    if (viewMode !== '3d') return;
    const preview = runPreviewBuild();
    if (!preview.analysisJson) return;
    setPreviewJson((current) => {
      if (
        current &&
        JSON.stringify(current.walls) === JSON.stringify(preview.analysisJson.walls) &&
        JSON.stringify(current.rooms) === JSON.stringify(preview.analysisJson.rooms) &&
        JSON.stringify(current.points) === JSON.stringify(preview.analysisJson.points)
      ) {
        return current;
      }
      return preview.analysisJson;
    });
  }, [editorState, viewMode, runPreviewBuild]);

  const fit2DContent = useCallback(
    (state = editorState) => {
      const drawingBounds = getFloorPlan2DBounds({ state, includeBackground: true });
      if (drawingBounds && viewportActionsRef.current.fitBounds) {
        viewportActionsRef.current.fitBounds(drawingBounds, {
          paddingRatio: 0.18,
          paddingPx: FIT_PADDING_PX,
        });
        return;
      }
      const coordBounds = state.scale?.coordinateBounds;
      if (coordBounds && viewportActionsRef.current.fitBounds) {
        viewportActionsRef.current.fitBounds(coordBounds, {
          paddingRatio: 0.12,
          paddingPx: FIT_PADDING_PX,
        });
        return;
      }
      viewportActionsRef.current.fit?.();
    },
    [editorState],
  );

  const initializeManualDrawingMode = useCallback(() => {
    setViewMode('2d');
    setPreviewJson(null);
    setPreviewError('');
    setActiveBuildTool(null);
    setLibraryMode(null);
    setSelectedOpeningPreset(null);
    handleActiveToolChange('select');
    requestAnimationFrame(() => fit2DContent());
  }, [fit2DContent, handleActiveToolChange]);

  const editorActionsRef = useRef({ initializeManualDrawingMode });
  editorActionsRef.current.initializeManualDrawingMode = initializeManualDrawingMode;

  useEffect(() => {
    if (!onRegisterEditorActions) return undefined;
    onRegisterEditorActions(editorActionsRef.current);
    return () => onRegisterEditorActions(null);
  }, [onRegisterEditorActions]);

  useLayoutEffect(() => {
    if (pendingFitOnModeSwitchRef.current !== '2d' || viewMode !== '2d') return undefined;
    pendingFitOnModeSwitchRef.current = null;
    const frameId = requestAnimationFrame(() => {
      fit2DContent();
    });
    return () => cancelAnimationFrame(frameId);
  }, [viewMode, fit2DContent, editorState]);

  useLayoutEffect(() => {
    if (pendingFitOnModeSwitchRef.current !== '3d' || viewMode !== '3d') return undefined;
    const frameId = requestAnimationFrame(() => {
      if (threePreviewFitRef.current) {
        threePreviewFitRef.current();
        pendingFitOnModeSwitchRef.current = null;
      }
    });
    return () => cancelAnimationFrame(frameId);
  }, [viewMode, previewJson]);

  const handleClear = useCallback(() => {
    setPreviewJson(null);
    setPreviewError('');
    setInternalMessage('');
    setViewMode('2d');
    setSelectedOpeningPreset(null);
    setLibraryMode(null);
    setActiveBuildTool(null);
    if (onClear) {
      onClear();
    } else {
      onChange?.(createEmptyDesignerState());
    }
    history.clearHistory();
    viewportActionsRef.current.resetInteraction?.();
  }, [history, onChange, onClear]);

  const handleExportJson = useCallback(() => {
    setInternalMessage('');
    if (onExport) {
      onExport(editorState);
      return;
    }
    const validation = runValidation();
    if (!validation.success || !validation.analysisJson) {
      setPreviewError(validation.errors[0] || 'Validation failed.');
      return;
    }
    downloadJson(exportFilename, validation.analysisJson);
    setInternalMessage('JSON exported.');
  }, [editorState, exportFilename, onExport, runValidation]);

  const handleCopyJson = useCallback(async () => {
    setInternalMessage('');
    if (onCopyJson) {
      await onCopyJson(editorState);
      return;
    }
    const validation = runValidation();
    if (!validation.success || !validation.analysisJson) {
      setPreviewError(validation.errors[0] || 'Validation failed.');
      return;
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(validation.analysisJson, null, 2));
      setInternalMessage('JSON copied to clipboard.');
    } catch {
      setPreviewError('Could not copy JSON to clipboard.');
    }
  }, [editorState, onCopyJson, runValidation]);

  const handleFit = useCallback(() => {
    if (viewMode === '3d') {
      threePreviewActionsRef.current?.fit3DToScreen?.() ?? threePreviewFitRef.current?.();
      return;
    }
    fit2DContent();
  }, [fit2DContent, viewMode]);

  const handleZoomIn = useCallback(() => {
    if (viewMode === '3d') {
      threePreviewActionsRef.current?.zoom3DIn?.();
      return;
    }
    viewportActionsRef.current.zoomIn?.();
  }, [viewMode]);

  const handleZoomOut = useCallback(() => {
    if (viewMode === '3d') {
      threePreviewActionsRef.current?.zoom3DOut?.();
      return;
    }
    viewportActionsRef.current.zoomOut?.();
  }, [viewMode]);

  const handleWallSettingsChange = useCallback(
    (patch) => {
      const next = setWallSettings(editorState, patch);
      history.commit(next);
    },
    [editorState, history],
  );

  const handleBackgroundStateChange = useCallback(
    (next) => {
      history.commit(next);
    },
    [history],
  );

  useEffect(() => {
    const onKeyDown = (e) => {
      const target = /** @type {HTMLElement | null} */ (e.target);
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        history.undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y' || e.key === 'Y') {
        e.preventDefault();
        history.redo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [history]);

  const combinedError = externalError || previewError;
  const combinedMessage = externalMessage || internalMessage;
  const resolvedDebugJson =
    debugJson ??
    (previewJson ? JSON.stringify(previewJson, null, 2) : JSON.stringify(editorState, null, 2));

  const buildUploadContent = enableBackgroundReferenceImage ? (
    <FloorPlanBackgroundImageControls
      background={editorState.backgroundReferenceImage ?? null}
      metersPerPixel={editorState.scale.metersPerPixel}
      editMode={backgroundEditMode}
      onEditModeChange={setBackgroundEditMode}
      state={editorState}
      onStateChange={handleBackgroundStateChange}
      onFitContent={fit2DContent}
    />
  ) : (
    sidebarTopContent
  );

  const openingPreset = selectedOpeningPreset
    ? {
        widthMeters: selectedOpeningPreset.widthMeters,
        heightMeters: selectedOpeningPreset.heightMeters,
        sillHeightMeters: selectedOpeningPreset.sillHeightMeters,
      }
    : null;

  void handleExportJson;
  void handleCopyJson;
  void resolvedDebugJson;

  const historyApi = useMemo(
    () => ({
      commit: history.commit,
      apply: history.apply,
      beginTransaction: history.beginTransaction,
      endTransaction: history.endTransaction,
      clearHistory: history.clearHistory,
    }),
    [
      history.commit,
      history.apply,
      history.beginTransaction,
      history.endTransaction,
      history.clearHistory,
    ],
  );

  const canvasSection = (
    <>
      {viewMode === '2d' ? (
        <FloorPlanDesignerCanvas
          state={editorState}
          onStateChange={history.apply}
          historyApi={historyApi}
          activeTool={activeTool}
          onActiveToolChange={handleActiveToolChange}
          openingPreset={openingPreset}
          snapToGrid={snapToGrid}
          snapToPoints={snapToPoints}
          snapGridMeters={snapGridMeters}
          snapTolerancePx={snapTolerancePx}
          onRegisterViewportActions={handleRegisterViewportActions}
          onRoomSelectionChange={onRoomSelectionChange}
          onRenderRoomRequest={onRenderRoomRequest}
          enableBackgroundReferenceImage={enableBackgroundReferenceImage}
          backgroundEditMode={backgroundEditMode}
          subcategoryPanelOpen={isSubcategoryPanelOpen}
        />
      ) : (
        <SelectedAreaThreePreview
          analysisJson={previewJson}
          imageWidth={editorState.canvasWidth}
          imageHeight={editorState.canvasHeight}
          visibleHeightM={visibleHeightM}
          hideDevControls
          showVerticalWallEdges={false}
          showHorizontalWallEdges={false}
          onClippingStateReady={handleClippingStateReady}
          onRegisterFit={(fit) => {
            threePreviewFitRef.current = fit;
            if (pendingFitOnModeSwitchRef.current === '3d') {
              requestAnimationFrame(() => {
                fit?.();
                pendingFitOnModeSwitchRef.current = null;
              });
            }
          }}
          onRegisterViewportActions={(actions) => {
            threePreviewActionsRef.current = actions;
          }}
        />
      )}
    </>
  );

  return (
    <>
      {combinedError ? (
        <p className="new-2d-project-error" role="alert">
          {combinedError}
        </p>
      ) : null}
      {combinedMessage ? (
        <p className="new-2d-project-success" role="status">
          {combinedMessage}
        </p>
      ) : null}

      <div
        className={`floor-plan-designer-layout floor-plan-designer-layout--cad${
          sidebarMode === 'render' ? ' floor-plan-designer-layout--render-sidebar' : ''
        }`}
      >
        {sidebarMode === 'render' ? (
          <>
            <FloorPlanDesignerSidebar
              mode={sidebarMode}
              renderContent={renderSidebarContent}
              activeTool={activeTool}
              onActiveToolChange={handleActiveToolChange}
              snapToGrid={snapToGrid}
              onSnapToGridChange={setSnapToGrid}
              snapToPoints={snapToPoints}
              onSnapToPointsChange={setSnapToPoints}
              snapGridMeters={snapGridMeters}
              onSnapGridMetersChange={setSnapGridMeters}
              snapTolerancePx={snapTolerancePx}
              onSnapToleranceChange={setSnapTolerancePx}
              designerState={editorState}
              onClear={handleClear}
              onExportJson={handleExportJson}
              onCopyJson={handleCopyJson}
              previewError={combinedError}
              exportMessage={combinedMessage}
              debugJson={resolvedDebugJson}
            />
            <section className="floor-plan-designer-main card" aria-label={ariaLabel}>
              {canvasSection}
            </section>
          </>
        ) : (
          <section className="floor-plan-designer-main card" aria-label={ariaLabel}>
            <FloorPlanWorkspace
              activeCategory={activeWorkspaceCategory}
              onCategoryChange={handleCategoryChange}
              isSubcategoryPanelOpen={isSubcategoryPanelOpen}
              onCollapseSubcategoryPanel={handleCollapseSubcategoryPanel}
              activeBuildTool={activeBuildTool}
              onBuildToolChange={handleBuildToolChange}
              libraryMode={libraryMode}
              onOpenLibrary={handleOpenLibrary}
              onLibraryBack={handleLibraryBack}
              onLibrarySelectItem={handleLibrarySelectItem}
              buildUploadContent={buildUploadContent}
              showBuildUpload
              snapToGrid={snapToGrid}
              onSnapToGridChange={setSnapToGrid}
              snapGridMeters={snapGridMeters}
              onSnapGridMetersChange={setSnapGridMeters}
              snapTolerancePx={snapTolerancePx}
              onSnapToleranceChange={setSnapTolerancePx}
              wallThicknessMeters={editorState.wallSettings.thicknessMeters}
              onWallThicknessChange={(value) => handleWallSettingsChange({ thicknessMeters: value })}
              thicknessDirection={editorState.wallSettings.thicknessDirection}
              onThicknessDirectionChange={(value) =>
                handleWallSettingsChange({ thicknessDirection: value })
              }
              onClear={handleClear}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              canUse3D={canUse3D}
              visibleHeightM={visibleHeightM}
              maxVisibleHeightM={maxVisibleHeightM}
              onVisibleHeightChange={setVisibleHeightM}
              onUndo={() => history.undo()}
              onRedo={() => history.redo()}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onFit={handleFit}
              canUndo={history.canUndo}
              canRedo={history.canRedo}
            >
              {canvasSection}
            </FloorPlanWorkspace>
          </section>
        )}
      </div>
    </>
  );
}

export default FloorPlanEditor;
